import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Table, Tag, Button, Space, Input, Checkbox, message, Empty, Alert, Spin } from 'antd';
import { SearchOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  ApproverProjectOption,
  ApproverUser,
  getApprovers,
  getApproverProjects,
  updateApproverProjects
} from '../services/approverService';

const ROLE_COLOR: Record<string, string> = {
  super_admin: 'red',
  project_admin: 'blue',
  audit_admin: 'green'
};

const ApproverManagement: React.FC = () => {
  const [approvers, setApprovers] = useState<ApproverUser[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState<ApproverUser | null>(null);

  const [projects, setProjects] = useState<ApproverProjectOption[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [originalIds, setOriginalIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [projectKeyword, setProjectKeyword] = useState('');

  const fetchApprovers = useCallback(async () => {
    setLoadingApprovers(true);
    try {
      const res = await getApprovers();
      if (res.success) {
        setApprovers(res.data || []);
      } else {
        message.error(res.message || '获取审批员列表失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取审批员列表失败');
    } finally {
      setLoadingApprovers(false);
    }
  }, []);

  const fetchApproverProjects = useCallback(async (userId: number) => {
    setLoadingProjects(true);
    try {
      const res = await getApproverProjects(userId);
      if (res.success) {
        setProjects(res.data.projects || []);
        setCheckedIds(res.data.assigned_project_ids || []);
        setOriginalIds(res.data.assigned_project_ids || []);
      } else {
        message.error(res.message || '获取审批项目失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取审批项目失败');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovers();
  }, [fetchApprovers]);

  const handleSelectApprover = (approver: ApproverUser) => {
    setSelectedApprover(approver);
    setProjectKeyword('');
    fetchApproverProjects(approver.id);
  };

  const toggleProject = (projectId: number, checked: boolean) => {
    setCheckedIds(prev =>
      checked ? [...prev, projectId] : prev.filter(id => id !== projectId)
    );
  };

  const visibleProjects = projects.filter(project => {
    if (!projectKeyword.trim()) return true;
    const keyword = projectKeyword.trim().toLowerCase();
    return (
      project.name.toLowerCase().includes(keyword) ||
      (project.manager || '').toLowerCase().includes(keyword)
    );
  });

  const handleSelectAllVisible = (checked: boolean) => {
    const visibleIds = visibleProjects.map(project => project.id);
    setCheckedIds(prev =>
      checked
        ? Array.from(new Set([...prev, ...visibleIds]))
        : prev.filter(id => !visibleIds.includes(id))
    );
  };

  const hasChanges =
    checkedIds.length !== originalIds.length ||
    checkedIds.some(id => !originalIds.includes(id));

  const handleSave = async () => {
    if (!selectedApprover) return;
    setSaving(true);
    try {
      const res = await updateApproverProjects(selectedApprover.id, checkedIds);
      if (res.success) {
        message.success(res.message || '保存成功');
        setOriginalIds(checkedIds);
        fetchApprovers();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const approverColumns: ColumnsType<ApproverUser> = [
    {
      title: '审批员',
      key: 'name',
      render: (_, record) => (
        <div>
          <div>{record.name || record.username}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{record.phone || '-'}</div>
        </div>
      )
    },
    {
      title: '角色',
      dataIndex: 'role_text',
      key: 'role_text',
      width: 110,
      render: (text: string, record) => (
        <Tag color={ROLE_COLOR[record.role] || 'default'}>{text}</Tag>
      )
    },
    {
      title: '负责项目',
      dataIndex: 'project_count',
      key: 'project_count',
      width: 100,
      render: (count: number) => `${count} 个`
    }
  ];

  const allVisibleChecked =
    visibleProjects.length > 0 && visibleProjects.every(project => checkedIds.includes(project.id));
  const someVisibleChecked =
    visibleProjects.some(project => checkedIds.includes(project.id)) && !allVisibleChecked;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="审批员管理"
        description="在这里可以一次性给某位审批员勾选他负责审批的多个项目。审批员登录后台后，问卷审批页面只会显示他负责的项目。若项目本身配置了「按角色审批」并命中该审批员的角色，即使这里没有勾选，他同样能审批该项目。"
      />

      <Row gutter={16}>
        <Col xs={24} lg={9}>
          <Card
            title="审批员列表"
            size="small"
            extra={
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchApprovers}>
                刷新
              </Button>
            }
          >
            <Table<ApproverUser>
              rowKey="id"
              columns={approverColumns}
              dataSource={approvers}
              loading={loadingApprovers}
              size="small"
              pagination={false}
              scroll={{ y: 520 }}
              rowClassName={record => (record.id === selectedApprover?.id ? 'ant-table-row-selected' : '')}
              onRow={record => ({
                onClick: () => handleSelectApprover(record),
                style: { cursor: 'pointer' }
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card
            title={
              selectedApprover
                ? `${selectedApprover.name || selectedApprover.username} 负责审批的项目`
                : '负责审批的项目'
            }
            size="small"
            extra={
              selectedApprover && (
                <Space>
                  <span style={{ color: '#999', fontSize: 12 }}>
                    已选 {checkedIds.length} 个
                  </span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={!hasChanges}
                    onClick={handleSave}
                  >
                    保存
                  </Button>
                </Space>
              )
            }
          >
            {!selectedApprover ? (
              <Empty description="请先在左侧选择一位审批员" />
            ) : (
              <Spin spinning={loadingProjects}>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Input
                    placeholder="按项目名称 / 负责人搜索"
                    prefix={<SearchOutlined />}
                    value={projectKeyword}
                    onChange={e => setProjectKeyword(e.target.value)}
                    style={{ width: 260 }}
                    allowClear
                  />
                  <Checkbox
                    checked={allVisibleChecked}
                    indeterminate={someVisibleChecked}
                    onChange={e => handleSelectAllVisible(e.target.checked)}
                  >
                    全选当前列表（{visibleProjects.length} 个）
                  </Checkbox>
                </Space>

                {visibleProjects.length === 0 ? (
                  <Empty description="没有匹配的问卷项目" />
                ) : (
                  <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                    {visibleProjects.map(project => (
                      <div
                        key={project.id}
                        style={{
                          padding: '8px 4px',
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Checkbox
                          checked={checkedIds.includes(project.id)}
                          onChange={e => toggleProject(project.id, e.target.checked)}
                        >
                          <span>{project.name}</span>
                          <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                            负责人：{project.manager || '-'}
                          </span>
                        </Checkbox>
                        {project.by_role && <Tag color="gold">角色审批已覆盖</Tag>}
                      </div>
                    ))}
                  </div>
                )}
              </Spin>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ApproverManagement;
